<template>
  <div class="bg-marketing-light pb-5">
    <!-- Breadcrumb Section -->
    <section class="bg-white py-3" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <div class="d-flex align-items-center small text-muted">
          <router-link to="/" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Home</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <router-link to="/calculators" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Calculators</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <span class="text-marketing-dark font-weight-bold">SOCSO & EIS Calculator</span>
        </div>
      </CContainer>
    </section>

    <!-- Hero Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <div class="marketing-icon-wrapper mx-auto mb-4" style="width: 80px; height: 80px; font-size: 2rem;">
              <CIcon name="cil-calculator" size="custom-size" style="width: 40px; height: 40px;" class="text-success"/>
            </div>
            <h1 class="display-4 font-weight-bold marketing-heading mb-3">
              SOCSO & EIS Calculator Malaysia 2026
            </h1>
            <p class="lead marketing-body mb-0">
              Free online SOCSO (PERKESO) and EIS (SIP) calculator for Malaysian employers and employees. Based on latest statutory rates up to RM6000 ceiling.
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Main Calculator Section -->
    <section class="py-5">
      <CContainer>
        <CRow>
          <!-- Left Column: Interactive Calculator -->
          <CCol lg="8" class="mb-4">
            <CCard class="border-0 shadow-sm h-100">
              <CCardBody class="p-4 p-md-5">
                <h2 class="h3 font-weight-bold text-marketing-dark mb-4">Calculate SOCSO & EIS</h2>

                <!-- Input: Salary -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark">Monthly Salary (RM)</label>
                  <div class="input-group input-group-lg">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-white border-right-0 text-muted font-weight-bold">RM</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control border-left-0 pl-0" 
                      v-model.number="salary"
                      @input="calculate"
                      placeholder="3000"
                      min="0"
                      step="100"
                      style="box-shadow: none;"
                    >
                  </div>
                  <small class="form-text text-muted mt-2">Enter your gross monthly salary before deductions</small>
                </div>

                <!-- Input: Age Range Buttons -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark mb-3">Employee Age</label>
                  <CRow>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'below60' ? 'success' : 'light'"
                         class="py-3 font-weight-bold h-100"
                         style="border-width: 2px;"
                         :class="{'border-success': ageRange === 'below60'}"
                         @click="selectAge('below60')"
                       >
                         Below 60 <br><small class="font-weight-normal">(Table 1 & EIS)</small>
                       </CButton>
                    </CCol>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="ageRange === 'above60' ? 'success' : 'light'"
                         class="py-3 font-weight-bold h-100"
                         style="border-width: 2px;"
                         :class="{'border-success': ageRange === 'above60'}"
                         @click="selectAge('above60')"
                       >
                         60 & Above <br><small class="font-weight-normal">(Table 2, No EIS)</small>
                       </CButton>
                    </CCol>
                  </CRow>
                </div>

                <!-- Results Panel (Active) -->
                <div v-if="salary > 0" class="bg-success text-white p-4 p-md-5 rounded-lg mt-5" style="background: linear-gradient(135deg, #10B981 0%, #059669 100%);">
                  <h4 class="h5 font-weight-bold mb-4 d-flex align-items-center">
                    <CIcon name="cil-check-circle" class="mr-2"/> Calculation Results Breakdown
                  </h4>

                  <!-- SOCSO -->
                  <h5 class="h6 font-weight-bold text-uppercase letter-spacing-1 border-bottom border-light pb-2 mb-3" style="border-color: rgba(255,255,255,0.2) !important;">SOCSO (PERKESO)</h5>
                  <CRow class="mb-4">
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employee Deduction</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employeeSocso.toFixed(2) }}</div>
                      </div>
                    </CCol>
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employer Contribution</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employerSocso.toFixed(2) }}</div>
                      </div>
                    </CCol>
                    <CCol md="4">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.25);">
                        <div class="small font-weight-bold mb-1 text-uppercase letter-spacing-1">Total SOCSO</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ (employeeSocso + employerSocso).toFixed(2) }}</div>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- EIS -->
                  <h5 class="h6 font-weight-bold text-uppercase letter-spacing-1 border-bottom border-light pb-2 mb-3 mt-4" style="border-color: rgba(255,255,255,0.2) !important;">EIS (SIP)</h5>
                  <CRow class="mb-4">
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employee Deduction</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employeeEis.toFixed(2) }}</div>
                      </div>
                    </CCol>
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.15);">
                        <div class="small opacity-75 mb-1 text-uppercase letter-spacing-1">Employer Contribution</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ employerEis.toFixed(2) }}</div>
                      </div>
                    </CCol>
                    <CCol md="4">
                      <div class="p-3 rounded h-100" style="background: rgba(255,255,255,0.25);">
                        <div class="small font-weight-bold mb-1 text-uppercase letter-spacing-1">Total EIS</div>
                        <div class="h3 font-weight-bold mb-0">RM {{ (employeeEis + employerEis).toFixed(2) }}</div>
                      </div>
                    </CCol>
                  </CRow>

                  <!-- Final Calculation Summary -->
                  <div class="pt-3 border-top" style="border-color: rgba(255,255,255,0.2) !important;">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="opacity-75">Gross Salary:</span>
                      <strong class="font-weight-bold">RM {{ salary.toFixed(2) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="opacity-75">Total Deducted (SOCSO + EIS):</span>
                      <strong class="font-weight-bold">RM {{ (employeeSocso + employeeEis).toFixed(2) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="opacity-75">Take-Home (Before Tax/EPF):</span>
                      <strong class="font-weight-bold h5 mb-0">RM {{ (salary - employeeSocso - employeeEis).toFixed(2) }}</strong>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="mt-5 p-5 border text-center rounded-lg" style="border-style: dashed !important; border-width: 2px !important; border-color: #cbd5e1 !important; background: #f8fafc;">
                   <CIcon name="cil-calculator" size="2xl" class="text-muted mb-3 opacity-50"/>
                   <p class="text-muted font-weight-600 mb-0">Enter your monthly salary above to calculate SOCSO and EIS contributions instantly.</p>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Right Column: Info Sidebar -->
          <CCol lg="4">
            <!-- Quick Facts Card -->
            <CCard class="border-0 shadow-sm mb-4">
              <CCardBody class="p-4">
                 <h3 class="h5 font-weight-bold text-marketing-dark mb-4">SOCSO & EIS Quick Facts</h3>

                 <div class="mb-4">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Salary Ceiling</h6>
                   <p class="text-muted small mb-0">The maximum salary cap for SOCSO and EIS calculation was increased to <strong>RM 6,000</strong> effective October 2024.</p>
                 </div>
                 
                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Age 60 & Above</h6>
                   <p class="text-muted small mb-1">Employees aged 60 and above only pay SOCSO under Table 2 (Employment Injury Scheme), which is borne entirely by the employer. They do not contribute to EIS.</p>
                 </div>

                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">EIS (SIP) Purpose</h6>
                   <p class="text-muted small">Helps workers who have lost their jobs. Contribution is roughly 0.2% from employee and 0.2% from employer.</p>
                 </div>

                 <div class="border-top pt-4 text-center mt-5">
                   <CButton block class="btn-marketing-accent py-3 font-weight-bold mb-2" to="/signup">
                     Automate with Niagaku
                   </CButton>
                   <small class="text-muted">Calculate all payroll instantly.</small>
                 </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Final CTA -->
    <section class="py-5 text-white text-center" style="background: linear-gradient(135deg, #10B981 0%, #059669 100%);">
      <CContainer class="py-4">
        <h2 class="display-4 font-weight-bold mb-4 text-white">Never Manually Check Tables Again</h2>
        <p class="lead mb-5 opacity-80 max-w-700 mx-auto" style="color: #ecfdf5;">Niagaku handles all Malaysian statutory tables directly in your payroll generation. Save hours of data entry and ensure 100% compliance automatically.</p>
        <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center" style="gap: 1rem;">
          <CButton class="bg-white text-success px-5 py-3 font-weight-bold" size="lg" to="/signup" style="border: none;">Start Free Trial</CButton>
          <CButton class="btn-outline-light-custom px-5 py-3 font-weight-bold" size="lg" to="/pricing">View Pricing</CButton>
        </div>
      </CContainer>
    </section>
  </div>
</template>

<script>
// We use a simplified approximation of the SOCSO tables for the marketing calculator
// For exact production-level payroll, the Niagaku backend table lookup is used.
export default {
  name: 'SocsoCalculator',
  metaInfo: {
    title: 'SOCSO & EIS Calculator Malaysia - Free PERKESO Contribution Calculator',
    meta: [
      {
        name: 'description',
        content: 'Free SOCSO (PERKESO) and EIS (SIP) calculator for Malaysia. Calculate employee and employer contributions instantly with latest RM6000 ceiling rates.'
      }
    ]
  },
  data() {
    return {
      salary: 3000,
      ageRange: 'below60',
      employeeSocso: 0,
      employerSocso: 0,
      employeeEis: 0,
      employerEis: 0
    }
  },
  mounted() {
    this.calculate()
  },
  methods: {
    selectAge(range) {
      this.ageRange = range
      this.calculate()
    },
    calculate() {
      if (!this.salary || this.salary <= 0) {
        this.employeeSocso = 0
        this.employerSocso = 0
        this.employeeEis = 0
        this.employerEis = 0
        return
      }

      // Max ceiling cap is RM 6000
      let cappedSalary = Math.min(this.salary, 6000);
      
      // SOCSO Estimation (Roughly Employee 0.5%, Employer 1.75% of capped salary but mapped to nearest RM0.50 bucket)
      // Because this is marketing, an approximation is acceptable if precise table lookup list is hundreds of lines. 
      // It is standard practice to approximate to nearest 0.25 RM boundaries for web calculators or build in logic like this:
      let socsoBracketCenter = cappedSalary; 
      
      if (this.ageRange === 'below60') {
         // Table 1: Invalidity & Employment Injury Scheme
         this.employeeSocso = (socsoBracketCenter * 0.005);
         this.employerSocso = (socsoBracketCenter * 0.0175);
         
         // Fix rounding exactly nearest to official tables format (simplified approximation)
         this.employeeSocso = Math.round((this.employeeSocso + Number.EPSILON) * 4) / 4; 
         this.employerSocso = Math.round((this.employerSocso + Number.EPSILON) * 20) / 20;

         // EIS Calculation (0.2% Employee, 0.2% Employer)
         this.employeeEis = cappedSalary * 0.002;
         this.employerEis = cappedSalary * 0.002;
         // Round up EIS
         this.employeeEis = Math.ceil((this.employeeEis + Number.EPSILON) * 10) / 10;
         this.employerEis = Math.ceil((this.employerEis + Number.EPSILON) * 10) / 10;
      } else {
         // Table 2: Employment Injury Scheme Only (Age 60+)
         this.employeeSocso = 0; // Employees don't pay
         this.employerSocso = (socsoBracketCenter * 0.0125);
         this.employerSocso = Math.round((this.employerSocso + Number.EPSILON) * 20) / 20;

         // No EIS for >60
         this.employeeEis = 0;
         this.employerEis = 0;
      }
    }
  }
}
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8fafc;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
.max-w-700 {
  max-width: 700px;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
.opacity-80 {
  opacity: 0.8;
}
.opacity-75 {
  opacity: 0.75;
}
.opacity-50 {
  opacity: 0.5;
}
</style>
